import { countryApi as api } from './countryApi';
export const addTagTypes = ['common', 'region', 'user'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      commonTimezonesList: build.query<
        CommonTimezonesListApiResponse,
        CommonTimezonesListApiArg
      >({
        query: queryArg => ({
          url: `/common/timezones/`,
          params: {
            search: queryArg.search,
            ordering: queryArg.ordering,
            limit: queryArg.limit,
            offset: queryArg.offset,
            country_name: queryArg.countryName
          }
        }),
        providesTags: ['common']
      }),
      regionCountryList: build.query<
        RegionCountryListApiResponse,
        RegionCountryListApiArg
      >({
        query: queryArg => ({
          url: `/region/country/`,
          params: {
            search: queryArg.search,
            ordering: queryArg.ordering,
            limit: queryArg.limit,
            offset: queryArg.offset
          }
        }),
        providesTags: ['region']
      }),
      regionCountryCreate: build.mutation<
        RegionCountryCreateApiResponse,
        RegionCountryCreateApiArg
      >({
        query: queryArg => ({
          url: `/region/country/`,
          method: 'POST',
          body: queryArg.countryList
        }),
        invalidatesTags: ['region']
      }),
      regionCountryDetailedList: build.query<
        RegionCountryDetailedListApiResponse,
        RegionCountryDetailedListApiArg
      >({
        query: queryArg => ({
          url: `/region/country/detailed/`,
          params: {
            search: queryArg.search,
            ordering: queryArg.ordering,
            limit: queryArg.limit,
            offset: queryArg.offset
          }
        }),
        providesTags: ['region']
      }),
      regionCountryDetailedCreate: build.mutation<
        RegionCountryDetailedCreateApiResponse,
        RegionCountryDetailedCreateApiArg
      >({
        query: queryArg => ({
          url: `/region/country/detailed_create/`,
          method: 'POST',
          body: queryArg.countryDetailed
        }),
        invalidatesTags: ['region']
      }),
      regionCountryRead: build.query<
        RegionCountryReadApiResponse,
        RegionCountryReadApiArg
      >({
        query: queryArg => ({ url: `/region/country/${queryArg.uuid}/` }),
        providesTags: ['region']
      }),
      regionCountryUpdate: build.mutation<
        RegionCountryUpdateApiResponse,
        RegionCountryUpdateApiArg
      >({
        query: queryArg => ({
          url: `/region/country/${queryArg.uuid}/`,
          method: 'PUT',
          body: queryArg.countryList
        }),
        invalidatesTags: ['region']
      }),
      regionCountryPartialUpdate: build.mutation<
        RegionCountryPartialUpdateApiResponse,
        RegionCountryPartialUpdateApiArg
      >({
        query: queryArg => ({
          url: `/region/country/${queryArg.uuid}/`,
          method: 'PATCH',
          body: queryArg.countryList
        }),
        invalidatesTags: ['region']
      }),
      regionCountryDelete: build.mutation<
        RegionCountryDeleteApiResponse,
        RegionCountryDeleteApiArg
      >({
        query: queryArg => ({
          url: `/region/country/${queryArg.uuid}/`,
          method: 'DELETE'
        }),
        invalidatesTags: ['region']
      }),
      regionCountryDetailedInstance: build.query<
        RegionCountryDetailedInstanceApiResponse,
        RegionCountryDetailedInstanceApiArg
      >({
        query: queryArg => ({
          url: `/region/country/${queryArg.uuid}/detailed/`
        }),
        providesTags: ['region']
      }),
      regionCountryDetailedUpdate: build.mutation<
        RegionCountryDetailedUpdateApiResponse,
        RegionCountryDetailedUpdateApiArg
      >({
        query: queryArg => ({
          url: `/region/country/${queryArg.uuid}/detailed_update/`,
          method: 'PATCH',
          body: queryArg.countryDetailed
        }),
        invalidatesTags: ['region']
      }),
      regionCountrySeedList: build.query<
        RegionCountrySeedListApiResponse,
        RegionCountrySeedListApiArg
      >({
        query: queryArg => ({
          url: `/region/country_seed/`,
          params: {
            search: queryArg.search,
            ordering: queryArg.ordering,
            limit: queryArg.limit,
            offset: queryArg.offset
          }
        }),
        providesTags: ['region']
      }),
      regionCountrySeedCreate: build.mutation<
        RegionCountrySeedCreateApiResponse,
        RegionCountrySeedCreateApiArg
      >({
        query: queryArg => ({
          url: `/region/country_seed/`,
          method: 'POST',
          body: queryArg.countrySeed
        }),
        invalidatesTags: ['region']
      }),
      regionTimezoneList: build.query<
        RegionTimezoneListApiResponse,
        RegionTimezoneListApiArg
      >({
        query: queryArg => ({
          url: `/region/timezone/`,
          params: {
            search: queryArg.search,
            ordering: queryArg.ordering,
            limit: queryArg.limit,
            offset: queryArg.offset
          }
        }),
        providesTags: ['region']
      }),
      userUsersList: build.query<UserUsersListApiResponse, UserUsersListApiArg>(
        {
          query: queryArg => ({
            url: `/user/users/`,
            params: {
              search: queryArg.search,
              ordering: queryArg.ordering,
              limit: queryArg.limit,
              offset: queryArg.offset,
              status: queryArg.status,
              email: queryArg.email,
              first_name: queryArg.firstName,
              last_name: queryArg.lastName,
              groups__name: queryArg.groupsName,
              groups__uuid: queryArg.groupsUuid,
              groups: queryArg.groups,
              is_deleted: queryArg.isDeleted,
              is_active: queryArg.isActive
            }
          }),
          providesTags: ['user']
        }
      )
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type RegionCountryListApiResponse = /** status 200 Custom Response */ {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: CountryListRead[];
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};

export type CommonTimezonesListApiResponse = /** status 200 Custom Response */ {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: TimezoneProfileRead[];
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type CommonTimezonesListApiArg = {
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Name of the country to filter timezones */
  countryName?: string;
};

export type RegionCountryListApiArg = {
  /** uuid */
  uuid?: string;
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Exclude zipcodes */
  exclude?: 'zipcodes';
};
export type RegionCountryCreateApiResponse = /** status 200 Custom Response */ {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: CountryListRead[];
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type RegionCountryCreateApiArg = {
  countryList: CountryList;
};
export type RegionCountryDetailedListApiResponse =
  /** status 200 Custom Response */ {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results: CountryListRead[];
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountryDetailedListApiArg = {
  /** uuid */
  uuid?: string;
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
};
export type RegionCountryDetailedCreateApiResponse =
  /** status 200 Custom Response */ {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results: CountryListRead[];
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountryDetailedCreateApiArg = {
  countryDetailed: CountryDetailed;
};
export type RegionCountryReadApiResponse = /** status 200 Custom Response */ {
  results: CountryListRead;
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type RegionCountryReadApiArg = {
  uuid: string;
};
export type RegionCountryUpdateApiResponse = /** status 200 Custom Response */ {
  results: CountryListRead;
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type RegionCountryUpdateApiArg = {
  uuid: string;
  countryList: CountryList;
};
export type RegionCountryPartialUpdateApiResponse =
  /** status 200 Custom Response */ {
    results: CountryListRead;
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountryPartialUpdateApiArg = {
  uuid: string;
  countryList: CountryList;
};
export type RegionCountryDeleteApiResponse = /** status 200 Custom Response */ {
  results: CountryListRead;
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type RegionCountryDeleteApiArg = {
  uuid: string;
};
export type RegionCountryDetailedInstanceApiResponse =
  /** status 200 Custom Response */ {
    results: CountryListRead;
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountryDetailedInstanceApiArg = {
  uuid: string;
};
export type RegionCountryDetailedUpdateApiResponse =
  /** status 200 Custom Response */ {
    results: CountryListRead;
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountryDetailedUpdateApiArg = {
  uuid: string;
  countryDetailed: CountryDetailed;
};
export type RegionCountrySeedListApiResponse =
  /** status 200 Custom Response */ {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results: CountrySeedRead[];
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountrySeedListApiArg = {
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
};
export type RegionCountrySeedCreateApiResponse =
  /** status 200 Custom Response */ {
    count?: number;
    next?: string | null;
    previous?: string | null;
    results: CountrySeedRead[];
    ok?: boolean;
    message?: {
      text?: string | null;
      code?: number;
      args?: object | null;
    };
  };
export type RegionCountrySeedCreateApiArg = {
  countrySeed: CountrySeed;
};
export type RegionTimezoneListApiResponse = /** status 200 Custom Response */ {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: TimezoneRead[];
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type RegionTimezoneListApiArg = {
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
};
export type UserUsersListApiResponse = /** status 200 Custom Response */ {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: UserRead[];
  ok?: boolean;
  message?: {
    text?: string | null;
    code?: number;
    args?: object | null;
  };
};
export type UserUsersListApiArg = {
  /** A search term. */
  search?: string;
  /** Which field to use when ordering the results. */
  ordering?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Filter users by status */
  status?: string;
  /** Filter by email */
  email?: string;
  /** Filter by first name */
  firstName?: string;
  /** Filter by last name */
  lastName?: string;
  /** Filter by group name */
  groupsName?: string;
  /** Filter by group UUID */
  groupsUuid?: string;
  /** Filter by groups */
  groups?: string;
  /** Filter by is_deleted */
  isDeleted?: boolean;
  /** Filter by is_active */
  isActive?: boolean;
};

export type TimezoneProfileRead = {
  uuid?: string;
  name?: string | null;
  offset?: string | null;
};

export type CountryList = {
  name?: string | null;
  co?: string;
  ordering?: string;
  director?: string[];
};
export type ProfileLiteRegion = {
  profile_pic?: string | null;
  cover_pic?: string | null;
};
export type ProfileLiteRegionRead = {
  uuid?: string;
  profile_pic?: string | null;
  cover_pic?: string | null;
};
export type RegionalDirector = {
  first_name?: string;
  last_name?: string;
  profile?: ProfileLiteRegion;
};
export type RegionalDirectorRead = {
  uuid?: string;
  first_name?: string;
  last_name?: string;
  profile?: ProfileLiteRegionRead;
};
export type StateLite = {
  name: string;
  st: string;
};
export type StateLiteRead = {
  uuid?: string;
  name: string;
  st: string;
};
export type Timezone = {
  name?: string | null;
  offset?: string | null;
  country?: string | null;
};
export type TimezoneRead = {
  uuid?: string;
  name?: string | null;
  offset?: string | null;
  country?: string | null;
};
export type CountryListRead = {
  uuid?: string;
  name?: string | null;
  co?: string;
  ordering?: string;
  director?: RegionalDirectorRead[];
  state?: StateLiteRead[];
  timezone?: TimezoneRead[];
  validation_details?: {
    zipcode?: string;
    state_label?: string;
  };
};
export type CountryDetailed = {
  name?: string | null;
  co?: string;
  ordering?: string;
  director?: {
    added?: string[];
    removed?: string[];
  };
  state?: string[];
  timezone?: string[];
};
export type CountryDetailedRead = {
  uuid?: string;
  name?: string | null;
  co?: string;
  ordering?: string;
  director?: string[];
  state?: string[];
  timezone?: string[];
};
export type CountrySeed = {
  name?: string | null;
  co: string;
};
export type CountrySeedRead = {
  name?: string | null;
  co: string;
  timezone?: TimezoneRead[];
};
export type ProfileList = {
  gender?: ('M' | 'F' | 'PNTS') | null;
  birth_date?: string | null;
  contact_number?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  country_origin?: string | null;
  completed_percentage?: number;
  zip_code?: string | null;
  profile_pic?: string;
  cover_pic?: string;
  volunteering_with?: boolean;
  mailing_address_same?: number;
  country_name?: string;
};
export type ProfileListRead = {
  uuid?: string;
  gender?: ('M' | 'F' | 'PNTS') | null;
  birth_date?: string | null;
  over_18?: string;
  contact_number?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  country_origin?: string | null;
  completed_percentage?: number;
  zip_code?: string | null;
  profile_pic?: string;
  cover_pic?: string;
  volunteering_with?: boolean;
  mailing_address_same?: number;
  country_name?: string;
};
export type GroupLite = {
  name: string;
};
export type GroupLiteRead = {
  uuid?: string;
  name: string;
};
export type OtherDetailsProfile = {
  cb_source?: string | null;
  cb_othersource?: string | null;
  cb_employer?: string | null;
  cb_organization?: string | null;
  cb_streetaddress?: string | null;
};
export type OtherDetailsProfileRead = {
  uuid?: string;
  cb_source?: string | null;
  cb_othersource?: string | null;
  cb_employer?: string | null;
  cb_organization?: string | null;
  cb_streetaddress?: string | null;
};
export type MamaProfile = {
  cb_mamaavail?: ('Active' | 'Paused') | null;
  cb_mamastart?: string | null;
  cb_pausedate?: string | null;
  cb_mamapausenotes?: string | null;
  cb_mamafreq?: string | null;
  cb_mamacap?: number | null;
  cb_mamafillin?: string | null;
  cb_mamaradius?: number | null;
  cb_mamacooks?: string | null;
  cb_mamasmsok?: number | null;
  cb_mamaleader?: string | null;
  cb_mamalat?: string | null;
  cb_mamalon?: string | null;
  cb_mamatzone?: string | null;
  cb_mamamisc?: string | null;
  cb_mamasocial?: string | null;
  cb_mamamatches?: number | null;
  cb_mamadeliveries?: number | null;
  cb_mamalmnotes?: string | null;
  cb_mama1stmatch?: string | null;
  cb_hidedelivered?: string | null;
  cb_mamaradiustype?: string | null;
};
export type MamaProfileRead = {
  uuid?: string;
  cb_mamaavail?: ('Active' | 'Paused') | null;
  cb_mamastart?: string | null;
  cb_pausedate?: string | null;
  cb_mamapausenotes?: string | null;
  cb_mamafreq?: string | null;
  cb_mamacap?: number | null;
  cb_mamafillin?: string | null;
  cb_mamaradius?: number | null;
  cb_mamacooks?: string | null;
  cb_mamasmsok?: number | null;
  cb_mamaleader?: string | null;
  cb_mamalat?: string | null;
  cb_mamalon?: string | null;
  cb_mamatzone?: string | null;
  cb_mamamisc?: string | null;
  cb_mamasocial?: string | null;
  cb_mamamatches?: number | null;
  cb_mamadeliveries?: number | null;
  cb_mamalmnotes?: string | null;
  cb_mama1stmatch?: string | null;
  cb_hidedelivered?: string | null;
  cb_mamaradiustype?: string | null;
};
export type User = {
  first_name?: string;
  last_name?: string;
  email: string;
  profile: ProfileList;
  groups?: GroupLite;
  other_details: OtherDetailsProfile;
  mamaprofile: MamaProfile;
  /** Designates whether this user should be treated as active. Unselect this instead of deleting accounts. */
  is_active?: boolean;
  block?: number;
};
export type UserRead = {
  uuid?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  date_joined?: string;
  profile: ProfileListRead;
  groups?: GroupLiteRead;
  other_details: OtherDetailsProfileRead;
  mamaprofile: MamaProfileRead;
  /** Designates whether this user should be treated as active. Unselect this instead of deleting accounts. */
  is_active?: boolean;
  block?: number;
  last_login?: string | null;
};
export const {
  useCommonTimezonesListQuery,
  useRegionCountryListQuery,
  useRegionCountryCreateMutation,
  useRegionCountryDetailedListQuery,
  useRegionCountryDetailedCreateMutation,
  useRegionCountryReadQuery,
  useRegionCountryUpdateMutation,
  useRegionCountryPartialUpdateMutation,
  useRegionCountryDeleteMutation,
  useRegionCountryDetailedInstanceQuery,
  useRegionCountryDetailedUpdateMutation,
  useRegionCountrySeedListQuery,
  useRegionCountrySeedCreateMutation,
  useRegionTimezoneListQuery,
  useUserUsersListQuery
} = injectedRtkApi;
